import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { CurrentUserService } from '@app/core/authentication/current.user';
import { SpecificationTemplatePermissions } from '@app/core/permissions';
import { FeatureToggleStatesService } from '@app/core/services/feature-toggle-states/feature-toggle-states.service';
import { isNullOrWhiteSpace } from 'cb-hub-lib';
import { includes } from 'lodash';
import { ISkinnySpecTemplateDto, ProductTierRangeEnumId } from '@classictechsolutions/hubapi-transpiled-enums';

enum Columns {
    TemplateName = 'templateName',
    Visibility = 'visibility',
    TierRange = 'tierRange',
    CreatedBy = 'createdBy',
    UpdatedBy = 'updatedBy',
    LastUpdated = 'lastUpdated',
    IsActive = 'isActive',
    Actions = 'actions',
}

@Component({
    selector: 'cb-spec-template-table',
    templateUrl: './spec-template-table.component.html',
    styleUrls: ['./spec-template-table.component.scss'],
    standalone: false
})
export class SpecTemplateTableComponent {
    @ViewChild(MatSort, { static: true }) public sort: MatSort;

    private _templates: ISkinnySpecTemplateDto[] = [];
    @Input() public set templates(items: ISkinnySpecTemplateDto[]) {
        this._templates = items;
        this.sortTemplates();
    }
    public get templates(): ISkinnySpecTemplateDto[] {
        return this._templates;
    }

    @Output() public readonly viewTemplate = new EventEmitter<ISkinnySpecTemplateDto>();
    @Output() public readonly editTemplate = new EventEmitter<ISkinnySpecTemplateDto>();
    @Output() public readonly duplicateTemplate = new EventEmitter<ISkinnySpecTemplateDto>();

    public readonly columnsEnum = Columns;
    public readonly displayedColumns = Object.values(Columns);
    public sortedTemplates: ISkinnySpecTemplateDto[] = [];
    protected readonly ProductTierRangeEnumId = ProductTierRangeEnumId;

    constructor(
        private readonly specTemplatePermissions: SpecificationTemplatePermissions,
        private readonly featureToggle: FeatureToggleStatesService,
        private readonly currentUserService: CurrentUserService,
    ) {
    }

    public sortTemplates(): void {
        const templates = this._templates.slice() ?? [];
        if (!this.sort?.active || isNullOrWhiteSpace(this.sort?.direction)) {
            this.sortedTemplates = templates;
            return;
        }
        this.sortedTemplates = templates.slice().sort((a, b) => {
            const isAsc = this.sort?.direction === 'asc';
            switch (this.sort.active) {
                case Columns.TemplateName: return this.compare(a.name, b.name, isAsc);
                case Columns.Visibility: return this.compare(this.getVisibility(a), this.getVisibility(b), isAsc);
                case Columns.TierRange: return this.compare(a.tierRange, b.tierRange, isAsc);
                case Columns.CreatedBy: return this.compare(a.createdByName, b.createdByName, isAsc);
                case Columns.UpdatedBy: return this.compare(a.updatedByName, b.updatedByName, isAsc);
                case Columns.LastUpdated: return this.compare(a.updatedDate, b.updatedDate, isAsc);
                default: return 0;
            }
        });
    }

    public canEdit(specTemplate: ISkinnySpecTemplateDto): boolean {
        let canEdit = false;
        if (this.specTemplatePermissions.canEditAll()) {
            canEdit = true;
        } else {
            canEdit = specTemplate.regionIds.some(i => {
                return includes(this.currentUserService.regionsids ?? [], i) && this.specTemplatePermissions.canEdit();
            });
        }
        return canEdit;
    }

    public canDuplicate(specTemplate: ISkinnySpecTemplateDto): boolean {
        return this.specTemplatePermissions.canDuplicate()
            && this.canEditSpecTemplateForFeatureToggle(specTemplate);
    }

    public getVisibility(specTemplate: ISkinnySpecTemplateDto): string {
        if (specTemplate.project) {
            return `Project ( ${specTemplate.project.projectName} )`;
        }
        return specTemplate.isRegional ? 'Regional' : 'National';
    }

    private compare(a: number | string, b: number | string, isAsc: boolean): number {
        return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }

    private canEditSpecTemplateForFeatureToggle(specTemplate: ISkinnySpecTemplateDto): boolean {
        return (this.featureToggle.isScheduleItemSpecTemplatesEnabled && specTemplate.isScheduleSpecTemplate)
            || (!this.featureToggle.isScheduleItemSpecTemplatesEnabled && !specTemplate.isScheduleSpecTemplate);
    }
}
